import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PaymentPage from "./PaymentPage";
import AddNewCardModal from "../BillingInformation/AddNewCardModal";
import { createStripeClientSecretNew, createSetupIntent } from "../../../../../../callable-cloud-functions/cloudFunctions";
import { useEmployer } from "../../../../../../data/employer";
import { manageConfiguration } from "../../../../../../data/configuration";

const appearance = { theme: "stripe" };
const loader = "auto";

type ElementsProviderProps = {
  selectedSubscription: string;
  employerId: string;
  promoCodeData: any;
  hasQuantity?: boolean;
};

const ElementsProvider = ({
  selectedSubscription,
  employerId,
  promoCodeData,
  hasQuantity = false,
}: ElementsProviderProps) => {
  const [clientSecret, setClientSecret] = useState("");
  const [fullyDiscounted, setFullyDiscounted] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDiscounted, setIsDiscounted] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [currentplan, setCurrentplan] = useState("");
  const [showAddCardModel, setShowAddCardModel] = useState(false);
  const [customerId, setCustomerId] = useState("");

  const isPromoCodeApplied = promoCodeData?.isPromoCodeApplied ?? false;
  const quantity = promoCodeData?.quantity ? Number(promoCodeData.quantity) : 1;
  const { data: employer } = useEmployer(employerId ?? "");

  const onCardSave = () => {
    setShowSuccessMessage(true);
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      setIsLoading(true);
      setPaymentError(null);

      try {
        const configData = await manageConfiguration();
        const publishableKey = configData[0]?.stripe_live
          ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE
          : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST;

        const stripe = await loadStripe(publishableKey || "");
        if (isMounted) setStripePromise(stripe);

        const result = await createStripeClientSecretNew({
          employerId,
          newSubscriptionPlan: selectedSubscription,
          isPromoCodeApplied,
          promotionCode: promoCodeData?.promotionCode?.code ?? "",
          quantity,
        });

        const data = result.data as any;

        if (isMounted) {
          setClientSecret(data?.clientSecret ?? "");
          setFullyDiscounted(data?.fullyDiscounted ?? false);
          setCurrentplan(data?.plan ?? "");
          setCustomerId(data?.customerId ?? "");
        }
      } catch (error: any) {
        console.error("Error setting up Stripe Elements:", error);
        if (isMounted) setPaymentError(error?.message ?? "Unknown error");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    employerId,
    isPromoCodeApplied,
    promoCodeData?.promotionCode?.code,
    quantity,
    selectedSubscription,
  ]);

  useEffect(() => {
    async function fetchSetupIntent() {
      setIsDiscounted(true);
      try {
        const result = await createSetupIntent({
          employerId: employerId,
          customerId: customerId,
        });

        const data = result.data as any;

        if (data?.clientSecret) {
          setClientSecret(data.clientSecret);
        }
      } catch (error) {
        setIsDiscounted(false);
        console.error("Error creating SetupIntent:", error);
      } finally {
        setIsDiscounted(false);
      }
    }

    if (fullyDiscounted && !clientSecret && currentplan === "monthly") {
      fetchSetupIntent();
      setShowAddCardModel(true);
    } else if (fullyDiscounted && !clientSecret && currentplan === "single") {
      setShowSuccessMessage(true);
    }
  }, [fullyDiscounted, clientSecret, currentplan, employerId, customerId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-gray-100 h-[70vh]">
        <div className="bg-white p-8 rounded shadow-md text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-800">
            Redirecting you to the payments page...
          </h1>
          <p className="mt-4 text-gray-600">
            Please do not refresh or close this page.
          </p>
        </div>
      </div>
    );
  }

  if (isDiscounted) {
    return (
      <div className="flex items-center justify-center bg-gray-100 h-[70vh]">
        <div className="bg-white p-8 rounded shadow-md text-center max-w-xl">
          <h1 className="text-2xl font-bold text-gray-800">
            Redirecting you to the card details page...
          </h1>
          <p className="mt-4 text-gray-600">
            Card details are required to set up monthly subscription. 
          </p>
          <p className="mt-4 text-gray-600">
            You'll be charged based on discount coupon offer.
          </p>
          <p className="mt-4 text-gray-600">
            Please do not refresh or close this page.
          </p>
        </div>
      </div>
    );
  }

  if (showSuccessMessage) {
    return (
      <div className="flex items-center justify-center p-10 bg-white">
        <div className="mt-[-50px] p-8 rounded text-center max-w-md">
          <div className="flex justify-center items-center">
            <img src="/images/publish_job.png" className="w-20 h-20" />
          </div>
          <h1 className="text-3xl font-bold text-instant-teams-blue-Main">
            Congratulations!
          </h1>
          <p className="text-gray-600 mt-2">
            Your Subscription Plan has been Activated
          </p>
        </div>
      </div>
    );
  }

  if (showAddCardModel && clientSecret && stripePromise) {
    return (
      <Elements
        options={{ clientSecret, appearance, loader }}
        stripe={stripePromise}
      >
        <AddNewCardModal
          employerId={employerId}
          customerId={customerId}
          onDone={onCardSave}
        />
      </Elements>
    );
  }

  if (clientSecret && stripePromise) {
    return (
      <Elements
        options={{ clientSecret, appearance, loader }}
        stripe={stripePromise}
      >
        <div className="w-full flex flex-row justify-start">
          <PaymentPage
            currentPlan={selectedSubscription}
            employerId={employerId}
            employer={employer}
            isPromoCodeApplied={isPromoCodeApplied}
            promoCodeData={promoCodeData}
            hasQuantity={hasQuantity}
          />
        </div>
      </Elements>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="mt-[-50px] p-8 rounded text-center max-w-md">
        <div className="flex justify-center items-center">
          <img src="/images/caution.png" className="w-20 h-20" />
        </div>
        <h1 className="text-3xl font-bold text-instant-teams-blue-Main">
          Oops! Something went wrong
        </h1>
        <p className="mt-4 text-gray-600">
          {/* {paymentError ?? "Unable to load payment info. Please try again."} */}
        </p>
      </div>
    </div>
  );
};

export default ElementsProvider;
